import type { UploadFile } from "antd/es/upload/interface";
import { eventAlbumSelectionUploadConstants as C } from "../actions/constants/eventAlbumSelectionUploadConstants";
import type { AlbumListedMediaItem } from "../services/eventAlbumSelectionUploadService";

export interface AlbumTemplateUploadState {
  stagedFiles: UploadFile[];
  uploading: boolean;
  uploadProgress: Record<string, number>;
  failedFiles: { fileName: string; error: string }[];
  uploadSuccess: boolean;
  uploadError: string | null;
  curatedMedia: AlbumListedMediaItem[];
  curatedMediaTotal: number;
  nextContinuationToken: string | null;
  hasMore: boolean;
  loadingMedia: boolean;
}

const initialTemplateState: AlbumTemplateUploadState = {
  stagedFiles: [],
  uploading: false,
  uploadProgress: {},
  failedFiles: [],
  uploadSuccess: false,
  uploadError: null,
  curatedMedia: [],
  curatedMediaTotal: 0,
  nextContinuationToken: null,
  hasMore: false,
  loadingMedia: false,
};

export interface AlbumUploadState {
  byTemplate: Record<string, AlbumTemplateUploadState>;
}

const initialState: AlbumUploadState = { byTemplate: {} };

const getOrInit = (state: AlbumUploadState, tplId: string): AlbumTemplateUploadState =>
  state.byTemplate[tplId] ?? { ...initialTemplateState };

const patchTemplate = (
  state: AlbumUploadState,
  tplId: string,
  patch: Partial<AlbumTemplateUploadState>
): AlbumUploadState => ({
  ...state,
  byTemplate: { ...state.byTemplate, [tplId]: { ...getOrInit(state, tplId), ...patch } },
});

export const albumUploadReducer = (state = initialState, action: any): AlbumUploadState => {
  const tplId: string | undefined = action.payload?.templateId;

  switch (action.type) {
    case C.stageAlbumFiles: {
      if (!tplId) return state;
      const cur = getOrInit(state, tplId);
      return patchTemplate(state, tplId, {
        stagedFiles: [...cur.stagedFiles, ...(action.payload.files as UploadFile[])],
      });
    }
    case C.removeAlbumStagedFile: {
      if (!tplId) return state;
      const cur = getOrInit(state, tplId);
      return patchTemplate(state, tplId, {
        stagedFiles: cur.stagedFiles.filter((f) => f.uid !== action.payload.fileUid),
      });
    }
    case C.clearAlbumStagedFiles: {
      if (!tplId) return state;
      return patchTemplate(state, tplId, { stagedFiles: [] });
    }
    case C.uploadAlbumImagesRequest: {
      if (!tplId) return state;
      return patchTemplate(state, tplId, {
        uploading: true, uploadSuccess: false, uploadError: null, uploadProgress: {}, failedFiles: [],
      });
    }
    case C.uploadAlbumImagesProgress: {
      if (!tplId) return state;
      const cur = getOrInit(state, tplId);
      const { fileName, percentage } = action.payload as { templateId: string; fileName: string; percentage: number };
      return patchTemplate(state, tplId, {
        uploadProgress: { ...cur.uploadProgress, [fileName]: percentage },
      });
    }
    case C.uploadAlbumImagesSuccess: {
      if (!tplId) return state;
      return patchTemplate(state, tplId, {
        uploading: false, uploadSuccess: true, uploadError: null,
        failedFiles: action.payload?.failed || [],
        stagedFiles: [], uploadProgress: {},
      });
    }
    case C.uploadAlbumImagesFailure: {
      if (!tplId) return state;
      return patchTemplate(state, tplId, {
        uploading: false,
        uploadError: typeof action.payload === "string" ? action.payload : action.payload?.message ?? "Upload failed",
        uploadSuccess: false,
      });
    }
    case C.listAlbumCuratedMediaRequest: {
      if (!tplId) return state;
      return patchTemplate(state, tplId, { loadingMedia: true, uploadError: null });
    }
    case C.listAlbumCuratedMediaSuccess: {
      if (!tplId) return state;
      const cur = getOrInit(state, tplId);
      const { items, total, nextContinuationToken, hasMore, append } = action.payload;
      return patchTemplate(state, tplId, {
        loadingMedia: false,
        curatedMedia: append ? [...cur.curatedMedia, ...(items || [])] : (items || []),
        curatedMediaTotal: total ?? (append ? cur.curatedMediaTotal : 0),
        nextContinuationToken: nextContinuationToken ?? null,
        hasMore: hasMore ?? false,
      });
    }
    case C.listAlbumCuratedMediaFailure: {
      if (!tplId) return state;
      return patchTemplate(state, tplId, { loadingMedia: false });
    }
    case C.resetAlbumTemplateState: {
      if (!tplId) return state;
      const { [tplId]: _, ...rest } = state.byTemplate;
      return { ...state, byTemplate: rest };
    }
    case C.clearAlbumUploadState:
      return initialState;
    default:
      return state;
  }
};

const forTemplate = (state: any, tplId: string): AlbumTemplateUploadState =>
  state.albumUpload?.byTemplate?.[tplId] ?? { ...initialTemplateState };

export const selectAlbumStagedFiles = (state: any, tplId: string): UploadFile[] => {
  return forTemplate(state, tplId).stagedFiles;
};
export const selectAlbumUploading = (state: any, tplId: string): boolean =>
  forTemplate(state, tplId).uploading;
export const selectAlbumUploadProgress = (state: any, tplId: string): Record<string, number> =>
  forTemplate(state, tplId).uploadProgress;
export const selectAlbumUploadSuccess = (state: any, tplId: string): boolean =>
  forTemplate(state, tplId).uploadSuccess;
export const selectAlbumUploadError = (state: any, tplId: string): string | null =>
  forTemplate(state, tplId).uploadError;
export const selectAlbumFailedFiles = (state: any, tplId: string) =>
  forTemplate(state, tplId).failedFiles;
export const selectAlbumCuratedMedia = (state: any, tplId: string): AlbumListedMediaItem[] =>
  forTemplate(state, tplId).curatedMedia;
export const selectAlbumCuratedMediaTotal = (state: any, tplId: string): number =>
  forTemplate(state, tplId).curatedMediaTotal;
export const selectAlbumLoadingMedia = (state: any, tplId: string): boolean =>
  forTemplate(state, tplId).loadingMedia;
export const selectAlbumHasMore = (state: any, tplId: string): boolean =>
  forTemplate(state, tplId).hasMore;
export const selectAlbumNextToken = (state: any, tplId: string): string | null =>
  forTemplate(state, tplId).nextContinuationToken;

export const selectIsAnyTemplateUploading = (state: any): boolean => {
  const byTemplate: Record<string, AlbumTemplateUploadState> =
    state.albumUpload?.byTemplate ?? {};
  for (const tpl of Object.values(byTemplate)) {
    if (tpl.uploading) return true;
  }
  return false;
};
